// Community selection gate between the landing page and the app shells.
// No community is listed until the user types a filter (name, city, or
// zip) -- GET /communities?q=... (community_store.search_communities) does
// the server-side matching.
//
// Clicking a result doesn't sign you in -- it drops down a username +
// password form right under that row. Both fields are verified server-side
// by POST /communities/select, which still checks one shared password per
// community (community_store.default_password(name) -- "password-" + the
// name, lowercased, spaces stripped, e.g. "Harmony" needs
// "password-harmony"; that formula is shown to admins elsewhere, see
// api.js's derivePassword) but now also reads `username` to decide *which*
// view loads:
//   - "admin" (the pre-filled default) -> the board/admin view.
//   - anything else -> treated as a resident's email and looked up in this
//     community's resident directory; a match signs in as that resident
//     and lands on the resident view by default (see BoardIQ.jsx's
//     ROLE_KEY, set here from the server's response before navigating in).
// The board/resident app only ever loads once that POST comes back OK --
// there's still no client-side check that lets it through early.
import React, { useEffect, useRef, useState } from "react";
import AdminFooter from "./AdminFooter.jsx";
import { api } from "./api.js";
import { Button, inputStyle } from "./ui.jsx";
import { ROLE_KEY } from "./BoardIQ.jsx";

const DEFAULT_USERNAME = "admin";

const DEBOUNCE_MS = 300;

export default function CommunityPicker({ onSelectCommunity, onBack }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Sign-in gate for whichever row is currently expanded.
  const [expanded, setExpanded] = useState(null); // community object, or null
  const [username, setUsername] = useState(DEFAULT_USERNAME);
  const [password, setPassword] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const q = query.trim();
    if (!q) {
      setResults([]);
      setStatus("idle");
      setError("");
      return;
    }

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("loading");
      setError("");
      api
        .listCommunities(q, controller.signal)
        .then((data) => {
          setResults((data && data.communities) || []);
          setStatus("ready");
        })
        .catch((err) => {
          if (err?.name === "AbortError") return;
          setError(err?.message || "Couldn't load communities.");
          setStatus("error");
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const toggleExpanded = (c) => {
    setAuthError("");
    setUsername(DEFAULT_USERNAME);
    setPassword("");
    setExpanded((prev) => (prev && prev.name === c.name ? null : c));
  };

  // Resident/board view only ever mounts (via onSelectCommunity -> the
  // parent router's go("app")) after this POST comes back OK -- there is
  // no client-side password check that lets it through early. The
  // response's `role` ("admin" or "resident") is what BoardIQ.jsx's
  // ROLE_KEY reads on mount to pick the default view, so it's persisted
  // here, before navigating in -- BoardIQ hasn't mounted yet at this point,
  // there's nothing to dispatch a live role-change event to.
  const submitLogin = async (c) => {
    if (authenticating) return;
    setAuthError("");
    setAuthenticating(true);
    try {
      const result = await api.selectCommunity(c.name, password, username.trim() || DEFAULT_USERNAME);
      try {
        localStorage.setItem(ROLE_KEY, result?.role === "resident" ? "resident" : "board");
      } catch { /* private mode */ }
      // communityId here is what session.js's startSession() (called by
      // App.jsx right after this) uses to set the X-Community-Id header for
      // the rest of the tab -- see api.py's POST /communities/select, which
      // now returns community_id alongside role/username/resident.
      onSelectCommunity({
        ...c,
        role: result?.role,
        username: result?.username,
        resident: result?.resident,
        communityId: result?.community_id,
      });
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setAuthError("Incorrect password for this community.");
      } else if (err?.status === 404 && username.trim().toLowerCase() !== DEFAULT_USERNAME) {
        setAuthError(`No resident on file with the email "${username.trim()}" for this community.`);
      } else {
        setAuthError(err?.message || "Couldn't sign you in.");
      }
      setAuthenticating(false);
    }
  };

  const metaFor = (c) => {
    const bits = [];
    if (c.city || c.zip) bits.push([c.city, c.zip].filter(Boolean).join(" "));
    bits.push(c.has_ccrs ? "CC&Rs on file" : "No CC&Rs yet");
    if (c.other_docs_count) bits.push(`${c.other_docs_count} other document(s)`);
    return bits.join(" · ");
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--ink)", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <header style={{ width: "100%", borderBottom: "2px solid var(--ink)", padding: "22px 40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <div style={{ width: 16, height: 16, background: "var(--accent)", flex: "none" }} />
          <span style={{ font: "800 18px/1 var(--bq-font-display)", letterSpacing: "-0.02em", color: "var(--ink)" }}>BoardIQ</span>
        </button>
      </header>

      <main style={{ width: "100%", maxWidth: 620, padding: "72px 24px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <h1 style={{ margin: 0, font: "800 36px/1.1 var(--bq-font-display)", letterSpacing: "-0.02em" }}>Choose your community</h1>
        <p style={{ margin: "14px 0 0", maxWidth: 460, font: "400 15px/1.55 var(--bq-font-body)", color: "var(--body)" }}>
          Search by name, city, or zip to find your association.
        </p>

        <div style={{ width: "100%", marginTop: 32, display: "flex", flexDirection: "column", gap: 2 }}>
          <button
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", background: "var(--accent)", color: "var(--onAccent)", border: "2px solid var(--ink)", padding: 18, font: "700 13px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", cursor: "pointer" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" /><path d="M5 12h14" />
            </svg>
            Onboard a new community
          </button>
        </div>

        <div style={{ width: "100%", marginTop: 28, textAlign: "left" }}>
          <label htmlFor="community-filter" style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted2)", display: "block", marginBottom: 6 }}>
            Name, city, or zip
          </label>
          <input
            id="community-filter"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setExpanded(null); }}
            placeholder="Start typing to search…"
            autoComplete="off"
            style={inputStyle}
          />
        </div>

        <div style={{ width: "100%", marginTop: 18, display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
          {!query.trim() && (
            <p style={{ margin: "8px 0 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)", textAlign: "center" }}>
              No communities are listed until you search.
            </p>
          )}

          {status === "loading" && (
            <p style={{ margin: "8px 0 0", font: "500 13px/1.5 var(--bq-font-body)", color: "var(--muted2)", textAlign: "center" }}>
              Searching…
            </p>
          )}

          {status === "error" && (
            <div role="alert" style={{ borderLeft: "3px solid var(--accent)", background: "var(--accentTint)", padding: "10px 12px", font: "500 12.5px/1.4 var(--bq-font-body)", color: "var(--accentDeep)" }}>
              {error}
            </div>
          )}

          {status === "ready" && results.length === 0 && (
            <p style={{ margin: "8px 0 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)", textAlign: "center" }}>
              No communities match "{query.trim()}".
            </p>
          )}

          {results.map((c) => {
            const isOpen = expanded && expanded.name === c.name;
            return (
              <div key={c.name}>
                <button
                  onClick={() => toggleExpanded(c)}
                  aria-expanded={isOpen}
                  style={{
                    display: "flex", alignItems: "center", gap: 14, width: "100%",
                    background: "var(--ink)", color: "var(--bg)", border: "2px solid var(--ink)", borderTop: "none",
                    padding: "18px 20px", textAlign: "left", cursor: "pointer",
                  }}
                >
                  <div style={{ flex: "none", width: 36, height: 36, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 21h18" /><path d="M6 21V7l6-4 6 4v14" /><path d="M10 21v-6h4v6" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ font: "700 16px/1.1 var(--bq-font-display)" }}>{c.name}</span>
                    <span style={{ font: "400 12px/1.3 var(--bq-font-display)", color: "var(--rule2)" }}>{metaFor(c)}</span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ flex: "none", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .15s ease" }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {isOpen && (
                  <div style={{ background: "var(--surface)", border: "2px solid var(--ink)", borderTop: "none", padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                    <label htmlFor={`community-username-${c.name}`} style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted2)" }}>
                      Username
                    </label>
                    <input
                      id={`community-username-${c.name}`}
                      type="text"
                      value={username}
                      autoComplete="username"
                      autoFocus
                      placeholder='"admin", or your resident email'
                      onChange={(e) => { setUsername(e.target.value); setAuthError(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") submitLogin(c); }}
                      style={inputStyle}
                    />
                    <p style={{ margin: 0, font: "400 11.5px/1.4 var(--bq-font-body)", color: "var(--muted2)" }}>
                      Use your registered email to login.
                    </p>

                    <label htmlFor={`community-password-${c.name}`} style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted2)", marginTop: 6 }}>
                      Password for {c.name}
                    </label>
                    <input
                      id={`community-password-${c.name}`}
                      type="password"
                      value={password}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      onChange={(e) => { setPassword(e.target.value); setAuthError(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") submitLogin(c); }}
                      style={inputStyle}
                    />
                    {authError && (
                      <div role="alert" style={{ borderLeft: "3px solid var(--accent)", background: "var(--accentTint)", padding: "10px 12px", font: "500 12.5px/1.4 var(--bq-font-body)", color: "var(--accentDeep)" }}>
                        {authError}
                      </div>
                    )}
                    <Button onClick={() => submitLogin(c)} disabled={authenticating || !password} style={{ minHeight: 44 }}>
                      {authenticating ? "Signing in…" : "Enter"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <AdminFooter static showAdminLink />
    </div>
  );
}
